/* Native Mini App Cloud: Figma preview card, keyboard and single-tap touch. */
(() => {
  if (window.MxMiniApps) return;
  const instances = new Map();
  function mount(root) {
    if (instances.has(root)) return;
    const controller = new AbortController(), options = {signal: controller.signal};
    const listen = (target, type, fn, extra = {}) => target?.addEventListener(type, fn, {...options, ...extra});
    const cloud = root.querySelector('[data-mini-cloud]');
    if (!cloud) return;
    const apps = [...cloud.querySelectorAll('.mx-mini-app')];
    const caption = root.querySelector('[data-os-caption-type="slide_2"]');
    const title = caption.querySelector('h3'), description = caption.querySelector('p');
    const defaultTitle = title.textContent, defaultDescription = description.textContent;
    const dialog = root.querySelector('.mx-mini-dialog'), dialogParent = dialog.parentNode;
    document.body.append(dialog); // Top-level dialog stays outside the theme's scaled canvas.
    const media = dialog.querySelector('.mx-mini-dialog-media');
    const preview = document.createElement('div');
    preview.className = 'mx-mini-dialog mx-mini-hover';
    preview.hidden = true;
    preview.id = dialog.getAttribute('aria-labelledby') + '-hover';
    preview.setAttribute('role', 'dialog');
    preview.replaceChildren(...[...dialog.childNodes].map(node => node.cloneNode(true)));
    preview.querySelector('h3').id = preview.id + '-title';
    preview.setAttribute('aria-labelledby', preview.querySelector('h3').id);
    const previewMedia = preview.querySelector('.mx-mini-dialog-media');
    previewMedia.tabIndex = 0;
    previewMedia.setAttribute('role', 'button');
    previewMedia.setAttribute('aria-label', 'Open full Mini App preview');
    document.body.append(preview);
    const reduced = matchMedia('(prefers-reduced-motion: reduce)'), mobile = matchMedia('(max-width: 767px)');
    let active = null, lastPointer = 'mouse', opener = null, revealTimer = 0, revealed = false;
    let bubble = null;
    let hideTimer = 0, intentTimer = 0, restoringFocus = false;
    let dismissHover = false, pointerX = 0, pointerY = 0;

    // Preserve the authored 24 positions. Additional apps use a centered staggered layout.
    const keys = new Set(apps.map(app => app.dataset.appKey));
    if (apps.length > 24 || keys.size !== apps.length) {
      const counts = apps.length > 35 ? [5,7,9,9,7,5] : [3,5,7,7,5,3,3,3];
      let offset = 0;
      counts.forEach((count, row) => {
        const n = Math.min(count, apps.length - offset);
        apps.slice(offset, offset + n).forEach((app, col) => {
          const x = 532 + (col - (n-1)/2) * 105, y = 38 + row * 71;
          const mx = 171 + (col - (n-1)/2) * (300/count), my = 22 + row * 51;
          [['x',x],['y',y],['s',app.dataset.featured==='true'?90:64],['mx',mx],['my',my],['ms',Math.min(44,285/count)]].forEach(([k,v])=>app.style.setProperty('--'+k,v));
        });
        offset += n;
      });
    }
    // Featured apps can be promoted into the larger authored slots without changing the cloud shape.
    const slots = apps.map(app => Object.fromEntries(['x','y','s','mx','my','ms'].map(k => [k, Number(app.style.getPropertyValue('--'+k))])));
    apps.forEach((app, i) => {
      if (app.dataset.featured !== 'true' || slots[i].s >= 108) return;
      const swap = apps.findIndex((other,j) => other.dataset.featured !== 'true' && slots[j].s >= 108);
      if (swap !== -1) [slots[i], slots[swap]] = [slots[swap], slots[i]];
    });
    apps.forEach((app,i) => {
      Object.entries(slots[i]).forEach(([k,v])=>app.style.setProperty('--'+k,v));
      const rows=[['weather','quick-settings','digital-pet'],['streamer-twitch','premiere','ai-assistant','photoshop','zoom'],['emulator','after-effects','obs','ai-progress','music'],['blender','mini-apps-menu','davinci-resolve','timer','system-monitor'],['soundboard','screen-mirroring','streamer-youtube']];
      const gridKey=app.dataset.appKey==='emulator-library'?'emulator':app.dataset.appKey;
      const row=rows.findIndex(keys=>keys.includes(gridKey));
      if(row>=0){app.style.setProperty('--grid-row',row+1);app.style.setProperty('--grid-col',rows[row].indexOf(gridKey)+(rows[row].length===3?2:1));}
      app.setAttribute('aria-expanded', 'false');
      app.setAttribute('aria-controls', preview.id);
      const distance = Math.hypot(slots[i].x - 532, slots[i].y - 264);
      app.style.setProperty('--reveal-delay',Math.round(distance*.48)+'ms');
    });
    function focusApp(app) {
      clearTimeout(intentTimer);
      clearTimeout(hideTimer);
      if (active === app) return;
      active?.classList.remove('is-focused');
      active?.setAttribute('aria-expanded', 'false');
      active = app;
      active?.classList.add('is-focused');
      active?.setAttribute('aria-expanded', 'true');
      active?.setAttribute('aria-controls', preview.id);
      title.textContent = app?.dataset.appName || defaultTitle;
      description.textContent = app?.dataset.description || defaultDescription;
      bubble?.cancel();
      if (!app) { preview.hidden = true; return; }
      if (!dialog.open && lastPointer !== 'touch' && lastPointer !== 'pen') {
        populatePreview(preview, app, false);
        preview.hidden = false;
        positionPreview();
        if (!reduced.matches) bubble = preview.animate([
          {opacity:0,transform:'scale(.72)',offset:0},
          {opacity:1,transform:'scale(1.025)',offset:.72},
          {opacity:1,transform:'scale(1)',offset:1}
        ],{duration:280,easing:'cubic-bezier(.2,.75,.25,1)'});
      }
    }
    function positionPreview() {
      if (preview.hidden || !active) return;
      const anchor = active.getBoundingClientRect();
      // Anchor to this icon's upper-right, including room for its 1.23 hover scale.
      // Flip only at a viewport edge; never return to the distant cloud sidebar.
      const w=preview.offsetWidth,h=preview.offsetHeight,gap=12+anchor.width*.115;
      const right=anchor.right+gap,left=anchor.left-w-gap;
      let x=right+w<=innerWidth-12?right:left;
      x=Math.max(12,Math.min(innerWidth-w-12,x));
      const y=Math.max(12,Math.min(innerHeight-h-12,anchor.top-h+24));
      preview.style.left=x+'px';preview.style.top=y+'px';
      preview.dataset.placement=x>=anchor.right?'top-right':'top-left';
      preview.style.transformOrigin=`${Math.max(0,Math.min(w,anchor.left+anchor.width/2-x))}px ${Math.max(0,Math.min(h,anchor.top+anchor.height/2-y))}px`;
    }
    function queueFocus(app){
      clearTimeout(intentTimer);
      if(active===app){clearTimeout(hideTimer);return}
      // Require a short deliberate hover on the actual icon, not a crossed empty area.
      intentTimer=setTimeout(()=>{
        const target=document.elementFromPoint(pointerX,pointerY)?.closest('.mx-mini-app');
        if(!dismissHover&&!dialog.open&&lastPointer==='mouse'&&target===app)focusApp(app);
      },120);
    }
    function scheduleHide() {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        const keyboardInside = lastPointer === 'keyboard' && (preview.contains(document.activeElement) || active === document.activeElement);
        if (!preview.matches(':hover') && !keyboardInside && !dialog.open) focusApp(null);
      }, 300);
    }
    function restoreFocus(app) {
      restoringFocus = true; app?.focus({preventScroll:true}); restoringFocus = false;
    }
    function dismissPreview() {
      const previous = active;
      // Removing an overlay can generate pointerenter beneath a stationary pointer.
      dismissHover = true; focusApp(null); restoreFocus(previous);
    }
    function resetLens() { apps.forEach(app => app.classList.remove('is-hovered')); }
    function populatePreview(card, app, expanded) {
      const a = app.dataset;
      card.querySelector('h3').textContent = a.appName;
      card.querySelector('.mx-mini-dialog-description').textContent = expanded ? a.expandedDescription : a.description;
      const creator = card.querySelector('.mx-mini-dialog-creator');
      creator.textContent = a.creator ? 'By '+a.creator : ''; creator.hidden = !a.creator;
      const link = card.querySelector('.mx-mini-dialog-link');
      const safeURL = /^(https?:\/\/|\/[^/])/.test(a.appUrl || '');
      link.hidden = !safeURL; if (safeURL) link.href = a.appUrl;
      const targetMedia = card.querySelector('.mx-mini-dialog-media');
      targetMedia.replaceChildren();
      if (expanded && a.video) {
        const video = document.createElement('video');
        video.src = a.video; video.controls = true; video.playsInline = true;
        video.poster = app.querySelector('.mx-mini-ui img').src;
        targetMedia.append(video);
      } else {
        const image = app.querySelector('.mx-mini-ui img').cloneNode();
        image.loading = 'eager'; targetMedia.append(image);
      }
    }
    function openPreview(app) {
      opener = app;
      app.setAttribute('aria-controls', dialog.id);
      preview.hidden = true;
      clearTimeout(hideTimer);
      populatePreview(dialog, app, true);
      dialog.showModal();
      dialog.querySelector('.mx-mini-close').focus({preventScroll:true});
    }
    apps.forEach(app => {
      listen(app,'pointerenter',e=>{lastPointer=e.pointerType;pointerX=e.clientX;pointerY=e.clientY;if(e.pointerType==='mouse'){app.classList.add('is-hovered');if(!dismissHover)queueFocus(app);}});
      listen(app,'pointerleave',e=>{app.classList.remove('is-hovered');clearTimeout(intentTimer);if(e.pointerType==='mouse' && !dialog.open)scheduleHide();});
      listen(app,'pointerdown',e=>{lastPointer=e.pointerType;});
      listen(app,'focus',()=>{if(lastPointer==='keyboard'&&!restoringFocus)focusApp(app);});
      listen(app,'blur',()=>{if(lastPointer==='keyboard' && !dialog.open)scheduleHide();});
      listen(app,'click',e=>{
        focusApp(app);openPreview(app);
      });
    });
    listen(document,'keydown',e=>{if(e.key==='Tab')lastPointer='keyboard';if(e.key==='Escape'&&!dialog.open&&!preview.hidden)dismissPreview();});
    listen(document,'pointermove',e=>{
      const moved=e.clientX!==pointerX||e.clientY!==pointerY;pointerX=e.clientX;pointerY=e.clientY;
      if(dismissHover&&moved){dismissHover=false;const app=e.target.closest('.mx-mini-app');if(app&&cloud.contains(app)&&e.pointerType==='mouse')queueFocus(app);}
    },{passive:true});
    listen(cloud,'pointerleave',()=>{resetLens();if(lastPointer==='mouse'&&!dialog.open)scheduleHide();});
    listen(preview,'pointerenter',()=>clearTimeout(hideTimer));
    listen(preview,'pointerleave',scheduleHide);
    listen(preview,'focusout',scheduleHide);
    listen(previewMedia,'click',()=>{if(active)openPreview(active);});
    listen(previewMedia,'keydown',e=>{if((e.key==='Enter'||e.key===' ')&&active){e.preventDefault();openPreview(active);}});
    listen(preview.querySelector('.mx-mini-close'),'click',dismissPreview);
    listen(window,'scroll',()=>{if(!preview.hidden)focusApp(null);},{passive:true});
    listen(window,'resize',positionPreview);
    listen(document,'pointerdown',e=>{if(!cloud.contains(e.target)&&!dialog.contains(e.target)&&!preview.contains(e.target))focusApp(null);},{passive:true});
    listen(dialog.querySelector('.mx-mini-close'),'click',()=>dialog.close());
    listen(dialog,'click',e=>{
      if(e.target!==dialog)return;
      const r=dialog.getBoundingClientRect();
      if(e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom)dialog.close();
    });
    listen(dialog,'close',()=>{media.querySelector('video')?.pause();media.replaceChildren();dismissHover=true;focusApp(null);restoreFocus(opener);});
    listen(reduced,'change',()=>{bubble?.cancel();resetLens();cloud.classList.remove('is-entering');});
    const reveal = () => {
      if(revealed)return;revealed=true;
      if(reduced.matches)return;
      cloud.classList.add('is-entering');
      revealTimer=setTimeout(()=>cloud.classList.remove('is-entering'),900);
    };
    const observer = new IntersectionObserver(entries=>{if(entries.some(e=>e.isIntersecting)){reveal();observer.disconnect();}},{threshold:.15});
    observer.observe(cloud);

    const tabs=[...root.querySelectorAll('[role="tab"]')], panels=[...root.querySelectorAll('.os-panel')], captions=[...root.querySelectorAll('.os-caption')];
    const indicator=root.querySelector('.os-tab-indicator'), rail=root.querySelector('.os-tabs-viewport');
    const navigation=document.createElement('nav');navigation.className='mx-os-navigation';navigation.setAttribute('aria-label','MASS OS pages');
    navigation.innerHTML='<button type="button" aria-label="Previous MASS OS page">←</button><span>Swipe tabs to explore</span><button type="button" aria-label="Next MASS OS page">→</button>';
    rail.after(navigation);
    const arrows=navigation.querySelectorAll('button');
    listen(arrows[0],'click',()=>selectSlide(Math.max(0,slide-1)));
    listen(arrows[1],'click',()=>selectSlide(Math.min(tabs.length-1,slide+1)));
    let slide=0,scrollFrame=0,scrollTop=44,scrollStep=1;
    const scrollTrack=root.closest('.mx-os-scroll');
    const scrollEnabled=()=>scrollTrack&&!document.body.classList.contains('mxw-editor');
    function readScroll(){
      if(!scrollEnabled()||scrollFrame)return;
      scrollFrame=requestAnimationFrame(()=>{
        scrollFrame=0;
        const distance=scrollTop-scrollTrack.getBoundingClientRect().top;
        const index=Math.max(0,Math.min(tabs.length-1,Math.floor(distance/scrollStep)));
        if(index!==slide)selectSlide(index,false,true);
      });
    }
    function sizeScroll(sync=true){
      if(!scrollTrack)return;
      scrollTrack.toggleAttribute('data-os-scroll',!!scrollEnabled());
      scrollTrack.toggleAttribute('data-mobile-scroll',mobile.matches&&!!scrollEnabled());
      scrollTop=parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--navigation-height'))||44;
      const view=innerHeight-scrollTop,scale=Math.min(document.documentElement.clientWidth/(mobile.matches?390:1440),view/(mobile.matches?Math.max(810,root.offsetHeight):916));
      // Width-based steps stay stable when a phone's browser bars change height.
      scrollStep=mobile.matches?Math.max(360,document.documentElement.clientWidth*1.5):Math.max(450,view*.85);
      scrollTrack.style.setProperty('--os-scale',scale);
      scrollTrack.style.setProperty('--os-view',view+'px');
      scrollTrack.style.setProperty('--os-height',(view+(scrollEnabled()?scrollStep*tabs.length:0))+'px');
      if(sync)readScroll();placeIndicator();
    }
    function placeIndicator() {
      if(!tabs[slide])return;
      arrows[0].disabled=slide===0;arrows[1].disabled=slide===tabs.length-1;
      navigation.querySelector('span').textContent=`${slide+1} / ${tabs.length} · ${mobile.matches&&scrollEnabled()?(slide===tabs.length-1?'Scroll to continue ↓':'Scroll to explore ↓'):'Swipe or tap arrows'}`;
      arrows[0].textContent=mobile.matches&&scrollEnabled()?'↑':'←';arrows[1].textContent=mobile.matches&&scrollEnabled()?'↓':'→';
      indicator.style.width=tabs[slide].offsetWidth+'px';
      indicator.style.transform='translateX('+tabs[slide].offsetLeft+'px)';
    }
    function selectSlide(index,keyboard=false,fromScroll=false) {
      slide=(index+tabs.length)%tabs.length;
      focusApp(null);resetLens();root.dataset.slide=String(slide);
      root.dataset.osActiveType=panels[slide].dataset.osType;
      root.querySelector('.os-captions').hidden=panels[slide].dataset.osType==='slide_7';
      tabs.forEach((tab,i)=>{tab.setAttribute('aria-selected',String(i===slide));tab.tabIndex=i===slide?0:-1;});
      panels.forEach((panel,i)=>{panel.classList.toggle('is-visible',i===slide);panel.inert=i!==slide;panel.setAttribute('aria-hidden',String(i!==slide));});
      captions.forEach((item,i)=>{item.classList.toggle('is-visible',i===slide);item.setAttribute('aria-hidden',String(i!==slide));});
      if(mobile.matches)sizeScroll(false);
      placeIndicator();
      rail.scrollTo({left:Math.max(0,tabs[slide].offsetLeft-(rail.clientWidth-tabs[slide].offsetWidth)/2),behavior:reduced.matches?'instant':'smooth'});
      if(keyboard)tabs[slide].focus({preventScroll:true});
      root.querySelector('.os-status').textContent=tabs[slide].textContent;
      // A tab seeks within the reserved track, never beyond a non-pinned section.
      // Start midway through its step so scrolling either way has equal room.
      if(!fromScroll&&scrollEnabled()&&getComputedStyle(scrollTrack.querySelector('.mx-os-pin')).position==='sticky')window.scrollTo({top:scrollY+scrollTrack.getBoundingClientRect().top-scrollTop+(slide+.5)*scrollStep,behavior:'instant'});
    }
    tabs.forEach((tab,i)=>listen(tab,'click',()=>selectSlide(i)));
    listen(root.querySelector('[role="tablist"]'),'keydown',e=>{
      const next={ArrowLeft:slide-1,ArrowRight:slide+1,Home:0,End:tabs.length-1}[e.key];
      if(next!==undefined){e.preventDefault();selectSlide(next,true);}
    });
    listen(window,'resize',sizeScroll);
    listen(mobile,'change',sizeScroll);
    listen(window,'scroll',readScroll,{passive:true});
    listen(reduced,'change',sizeScroll);
    listen(root,'shopify:block:select',e=>{
      const app=apps.find(a=>a.getAttribute('data-shopify-editor-block')?.includes(e.detail.blockId));
      if(app){selectSlide(panels.findIndex(p=>p.contains(cloud)));focusApp(app);}
    });
    root.mxSelect=selectSlide;
    selectSlide(0,false,true);sizeScroll();
    document.fonts.ready.then(()=>{if(root.isConnected)sizeScroll();});
    instances.set(root,()=>{
      scrollTrack?.removeAttribute('data-os-scroll');scrollTrack?.removeAttribute('data-mobile-scroll');navigation.remove();controller.abort();observer.disconnect();bubble?.cancel();resetLens();cancelAnimationFrame(scrollFrame);clearTimeout(revealTimer);clearTimeout(hideTimer);clearTimeout(intentTimer);preview.remove();
      if(dialog.open)dialog.close();media.replaceChildren();dialogParent.append(dialog);delete root.mxSelect;
      instances.delete(root);
    });
  }
  function scan(container=document){container.querySelectorAll('[data-mini-app-os]').forEach(mount);}
  window.MxMiniApps={scan};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>scan(),{once:true});else scan();
  document.addEventListener('shopify:section:load',e=>scan(e.target));
  document.addEventListener('shopify:section:unload',e=>{for(const [root,cleanup] of instances)if(e.target.contains(root))cleanup();});
})();
